import React, { useState, useMemo, memo } from "react";
import styles from "./simulazionePerformance.module.scss";
import { useTranslation } from "react-i18next";
import colorsStore from "shared/store/colorsStore";
import { formatItalianNumber } from "utils/formatUtils";

type SimulazionePerformanceProps = {
  data: any;
  title: string;
};

const SimulazionePerformanceComponent: React.FC<
  SimulazionePerformanceProps
> = ({ data, title = "" }) => {
  const dataSimulazione =
    data.analisi_backtest.simulazione_performance_indicatori_portafoglio;

  const outputData = dataSimulazione.rendimento.map(({ label, weight }) => {
    const vol = dataSimulazione.volatilita.find((v) => v.label === label);
    const sharpe = dataSimulazione.sharpe.find((s) => s.label === label);

    return {
      label,
      rendimento: weight,
      volatilita: vol ? vol.weight : null,
      sharpe: sharpe ? sharpe.weight : null,
    };
  });

  const { t } = useTranslation();
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const [columns] = useState(4);
  const totalRows = dataSimulazione.rendimento.length + 1;
  const rows = [];

  for (let rowIndex = 0; rowIndex < totalRows; rowIndex++) {
    const row = rowIndex + 1;

    rows.push(row);
  }
  const cells = useMemo(() => {
    const generatedCells = [];
    for (const row of rows) {
      for (let colIndex = 0; colIndex < columns; colIndex++) {
        const cell = {
          row,
          col: colIndex + 1,
        };

        generatedCells.push(cell);
      }
    }

    return generatedCells;
  }, [rows, columns]);
  const dashValue = (dataValue: any): any => {
    if (!dataValue || dataValue === undefined) {
      return "-";
    }
    if (typeof dataValue === "string") {
      return dataValue;
    }
    return formatItalianNumber(dataValue);
  };

  const renderedCells = useMemo(() => {
    return cells.map((cell) => {
      const { row, col } = cell;

      if (row === 1) {
        return (
          <div
            key={`${row}-${col}`}
            className={[styles.cell, styles.productName].join(" ")}
          >
            {col === 2 && t("portfolio.simulazioneRendimento")}
            {col === 3 && t("portfolio.simulazioneVolatility")}
            {col === 4 && t("portfolio.simulazioneSharpe")}
          </div>
        );
      }
      if (row > 1) {
        if (col === 1 && row > 1) {
          return (
            <div
              key={`${row}-${col}`}
              className={[
                styles.cellKeys,
                row === totalRows
                  ? styles.cellWithNoBorder
                  : styles.cellWithBorder,
              ].join(" ")}
            >
              {row > 1 && outputData[row - 2].label}
            </div>
          );
        }

        if (col > 1 && row > 1) {
          return (
            <div key={`${row}-${col}`}>
              <div className={styles.cellValueInnerHeader}>
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                    }}
                  >
                    <div
                      className={[
                        styles.cellValueInnerHeaderValue,
                        row === totalRows
                          ? styles.cellWithNoBorder
                          : styles.cellWithBorder,
                      ].join(" ")}
                      style={{
                        textAlign: "right",
                        lineHeight: "40px",
                        color: colorsConfig.FinecoBlack,
                      }}
                    >
                      {row === totalRows ? "" : ""}
                      {col === 2 && dashValue(outputData[row - 2].rendimento)}
                      {col === 3 && dashValue(outputData[row - 2].volatilita)}
                      {col === 4 && dashValue(outputData[row - 2].sharpe)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        }
      }

      return (
        <div key={`${row}-${col}`} className={styles.cellValue}>
          -
        </div>
      );
    });
  }, [cells, data]);

  return (
    <div>
      {title && <div className={styles.title}>{title}</div>}
      <div className={styles.container}>
        <div className={`${styles.grid} ${styles[`grid${columns}`]}`}>
          {renderedCells}
        </div>
      </div>
    </div>
  );
};

export const SimulazionePerformance = memo(
  SimulazionePerformanceComponent,
  (prevProps, nextProps) => {
    return (
      JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data) &&
      prevProps.title === nextProps.title
    );
  },
);

export default SimulazionePerformance;
